import { useEffect, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { collectionsApi } from '../../lib/api';

/**
 * "Save to a collection" for a record or a highlight: tick the collections
 * it belongs in, or make a new one. Collections are the person's own named
 * sets ("About my brother", "Things to ask the council").
 */
export const SaveToCollection = ({ targetType, targetId, orgId, label = 'Save to a collection', size = 'sm', variant = 'secondary', testId = 'save-to-collection' }) => {
  const [open, setOpen] = useState(false);
  const [collections, setCollections] = useState(null);
  const [locked, setLocked] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    collectionsApi.list()
      .then((r) => { setLocked(!!r.data?.locked); setCollections(r.data?.collections || []); })
      .catch(() => setCollections([]));
  }, [open]);

  const itemIn = (c) => c.items.find((i) => i.target_type === targetType && i.target_id === targetId);

  const toggle = async (c) => {
    setBusy(true);
    try {
      const existing = itemIn(c);
      if (existing) {
        await collectionsApi.removeItem(c.id, existing.id);
        setCollections((cs) => cs.map((x) => (x.id === c.id ? { ...x, items: x.items.filter((i) => i.id !== existing.id) } : x)));
      } else {
        const r = await collectionsApi.addItem(c.id, { target_type: targetType, target_id: targetId, org_id: orgId || null });
        setCollections((cs) => cs.map((x) => (x.id === c.id ? { ...x, items: [...x.items, { ...r.data, target_type: targetType, target_id: targetId }] } : x)));
      }
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const c = (await collectionsApi.create(newName.trim())).data;
      const r = await collectionsApi.addItem(c.id, { target_type: targetType, target_id: targetId, org_id: orgId || null });
      setCollections((cs) => [...(cs || []), { ...c, items: [{ ...r.data, target_type: targetType, target_id: targetId }] }]);
      setNewName('');
    } finally {
      setBusy(false);
    }
  };

  const savedCount = (collections || []).filter(itemIn).length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size={size} variant={variant} data-testid={testId}>
          {savedCount ? `Saved in ${savedCount} collection${savedCount === 1 ? '' : 's'}` : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3" align="start" data-testid={`${testId}-panel`}>
        <p className="text-[15px] font-semibold text-ink">Save to a collection</p>
        {locked ? (
          <p className="text-sm text-ink-55">Unlock your account to use your collections.</p>
        ) : collections === null ? (
          <p className="text-sm text-ink-55">Opening your collections…</p>
        ) : (
          <>
            {collections.length > 0 ? (
              <ul className="space-y-1 max-h-60 overflow-y-auto">
                {collections.map((c) => (
                  <li key={c.id}>
                    <label className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-ground cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-brand"
                        checked={!!itemIn(c)}
                        onChange={() => toggle(c)}
                        disabled={busy}
                        data-testid={`${testId}-toggle-${c.id}`}
                      />
                      <span className="text-[15px] text-ink">{c.name}</span>
                      <span className="ml-auto text-sm text-ink-55">{c.items.length}</span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-ink-55">You haven’t made a collection yet. Name one to start — for example “About my brother” or “Things to ask the council”.</p>
            )}
            <div className="flex gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
                placeholder="New collection"
                aria-label="Name a new collection"
                data-testid={`${testId}-new`}
              />
              <Button size="sm" onClick={create} disabled={busy || !newName.trim()} data-testid={`${testId}-create`}>Add</Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default SaveToCollection;
